/**
 * Lead record page — `lead-detail.html` ported.
 *
 * The prototype's three regions, in the prototype's own classes: a `.crumb`
 * bar, the `.hl-panel` identity header with its action row, a `.path` sales
 * stepper with a collapsible guidance panel underneath, then three columns —
 * score and related lists on the left, the working tabs in the centre,
 * engagement and suggestions on the right. `LeadDetailViews.jsx` holds
 * everything that goes inside those columns; this file is the shell, the path,
 * the header actions and their modals (Delete, Convert, Change Owner).
 */
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useLead, useConvertLead, useUpdateLead, useDeleteLead, useAssignLeads, useLeadOptions } from './useLeads.js';
import { useAuth } from '../../hooks/useAuth.js';
import { isMock } from '../../api/mockFallback.js';
import { PageLoader, Card, Avatar, ErrorState, EmptyState, MockBadge } from '../../components/common/index.jsx';
import { DeleteLeadModal, ChangeOwnerModal, NotBuiltModal, LeadFormModal } from './LeadModals.jsx';
import { ConvertModal } from './LeadDetailModals.jsx';
import { AddToCampaignModal } from '../campaigns/CampaignModals.jsx';
import { EmailComposerModal } from '../emailTemplates/EmailComposerModal.jsx';
import { ActivityTab } from '../activities/ActivityTimeline.jsx';
import {
  ScoreCard, RelatedLists, EngagementCard, NextStepCard, DuplicateCard,
  DetailsTab, NewsTab,
} from './LeadDetailViews.jsx';
import { toRow } from './leadShape.js';

const RATING_TONE = {
  Hot: ['pill-rose', 'bi-fire'], Warm: ['pill-amber', 'bi-thermometer-half'], Cold: ['pill-sky', 'bi-snow'],
};

/* The lifecycle a lead walks through before Converted — Unqualified and Junk
   are dead ends off to the side, not later stops on this path, same as the
   prototype's own fixed `PATH` array. */
const PATH = ['New', 'Working', 'Nurturing', 'Qualified', 'Converted'];

const TABS = [
  { key: 'activity', label: 'Activity' },
  { key: 'details', label: 'Details' },
  { key: 'news', label: 'News' },
];

/** Verbs the header offers that §4 documents but does not implement.
 *  Change Owner is not in here — see `canAssign` and `ChangeOwnerModal` below;
 *  it calls the live `POST /leads/{id}/assign`, gated `leads:assign`. `email`
 *  used to live here too — §8 is real now, so the header button opens
 *  `EmailComposerModal` instead. */
const NOT_BUILT = {
  clone: {
    title: 'Clone is not available',
    why: 'Duplicating a lead as a starting point for a related contact at the same company is its own endpoint, not built yet.',
    endpoint: 'POST /leads/{id}/clone',
    section: 'docs/API.md §4, answers 501',
  },
};

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();

  const { data: raw, isLoading, error, refetch } = useLead(id);
  const options = useLeadOptions();
  const convert = useConvertLead();
  const update = useUpdateLead();
  const remove = useDeleteLead();
  const assign = useAssignLeads();

  const [tab, setTab] = useState('activity');
  const [convertOpen, setConvertOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [assigningOpen, setAssigningOpen] = useState(false);
  const [notBuilt, setNotBuilt] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [campaignAddOpen, setCampaignAddOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(true);
  const [edits, setEdits] = useState(() => new Map());
  const dupRef = useRef(null);

  if (isLoading) return <PageLoader label="Loading lead" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  if (!raw) return <EmptyState icon="bi-question-circle" title="No such lead" />;

  /* Through the shared normaliser the list uses — see `leadShape.js`. This page
     reads `full_name`, `company_name`, `status.name`, `score_breakdown`, none of
     which are fixture field names, so every panel but the score would render
     blank without it. */
  const lead = toRow(raw);
  const isConverted = lead.converted;
  const canEdit = can('leads', 'edit') && !isConverted;
  const rating = RATING_TONE[lead.rating] ?? RATING_TONE.Cold;

  const pathIndex = PATH.indexOf(lead.status);
  /* Stops one short of Converted, deliberately. A plain PATCH can set
     status_id to a status flagged is_converted without ever running
     POST /leads/{id}/convert — no account, contact or opportunity gets
     created, `converted_at` stays null, but `status.is_converted` flips true
     and this page's own isConverted check then locks Edit and Convert with no
     way back through the UI. Reaching Converted has to go through the Convert
     button, which is the only thing offering a way out again. */
  const canAdvance = canEdit && pathIndex >= 0 && pathIndex < PATH.length - 2;
  const advance = async () => {
    const nextLabel = PATH[pathIndex + 1];
    const nextStatus = options.statuses.find((s) => s.label === nextLabel);
    if (!nextStatus) return;
    await update.mutateAsync({ id: lead.id, patch: { status_id: nextStatus.value }, rowVersion: lead.raw.row_version });
  };

  const onEdit = (key, value) => {
    if (value === undefined) {
      setEdits((prev) => { const next = new Map(prev); next.delete(key); return next; });
      return;
    }
    const field = key.slice(key.indexOf('.') + 1);
    const cast = ['employee_count', 'estimated_value'].includes(field) ? Number(value) : value;
    setEdits((prev) => new Map(prev).set(key, value));
    update.mutateAsync({ id: lead.id, patch: { [field]: cast }, rowVersion: lead.raw.row_version })
      .finally(() => setEdits((prev) => { const next = new Map(prev); next.delete(key); return next; }));
  };

  return (
    <>
      <div className="crumb mb-2">
        <Link to="/leads">Leads</Link><i className="bi bi-chevron-right" style={{ fontSize: 9 }} aria-hidden="true" />
        <span>{lead.name}</span>
        <span className="ms-2 pill pill-gray">{lead.lead_no}</span>
        {isMock(raw) && <MockBadge section="leads" />}
      </div>

      {/* ---- Highlights ---- */}
      <div className="hl-panel fade-up">
        <div className="d-flex align-items-start gap-3 flex-wrap">
          <div className="hl-icon"><i className="bi bi-magnet-fill" aria-hidden="true" /></div>
          <div className="min-w-0">
            <div className="eyebrow">Lead</div>
            <h1 className="mb-0" style={{ fontSize: 'var(--fs-xl)', letterSpacing: '-.035em' }}>{lead.name}</h1>
            <div className="t-xs ink-3 mt-1">
              <span>{lead.title} · {lead.company}</span>
              {lead.rating && (
                <span className={`pill ms-2 ${rating[0]}`}><i className={`bi ${rating[1]}`} aria-hidden="true" />{lead.rating}</span>
              )}
            </div>
          </div>
          <div className="ms-auto d-flex flex-wrap gap-2">
            {lead.phone
              ? <a className="btn btn-soft btn-sm-x" href={`tel:${lead.phone}`}><i className="bi bi-telephone me-1" aria-hidden="true" />Call</a>
              : <button type="button" className="btn btn-soft btn-sm-x" disabled><i className="bi bi-telephone me-1" aria-hidden="true" />Call</button>}
            <button type="button" className="btn btn-soft btn-sm-x" onClick={() => setEmailOpen(true)}>
              <i className="bi bi-envelope me-1" aria-hidden="true" />Email
            </button>
            {canEdit && (
              <button type="button" className="btn btn-edit btn-sm-x" onClick={() => setEditOpen(true)}>
                <i className="bi bi-pencil me-1" aria-hidden="true" />Edit
              </button>
            )}
            {can('leads', 'convert') && !isConverted && (
              <button type="button" className="btn btn-add btn-sm-x" onClick={() => setConvertOpen(true)}>
                <i className="bi bi-arrow-right-circle me-1" aria-hidden="true" />Convert
              </button>
            )}
            <HeaderMenu
              canAssign={can('leads', 'assign')}
              onOwner={() => setAssigningOpen(true)}
              onClone={() => setNotBuilt(NOT_BUILT.clone)}
              onCampaign={() => setCampaignAddOpen(true)}
              onFindDuplicates={() => dupRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              onDelete={can('leads', 'delete') ? () => setDeleting(true) : null}
            />
          </div>
        </div>

        <div className="hl-fields">
          <div><div className="k">Lead Score</div><div className="v">{lead.score ?? '—'}</div></div>
          <div><div className="k">Company</div><div className="v">{lead.company ?? '—'}</div></div>
          <div><div className="k">Phone</div><div className="v">{lead.phone ?? '—'}</div></div>
          <div><div className="k text-truncate" style={{ maxWidth: 200 }}>Email</div><div className="v text-truncate" style={{ maxWidth: 200 }}>{lead.email ?? '—'}</div></div>
          <div><div className="k">Est. Value</div><div className="v">{lead.value ? `$${Math.round(lead.value).toLocaleString()}` : '—'}</div></div>
          <div>
            <div className="k">Lead Owner</div>
            <div className="v d-flex align-items-center gap-1">
              {lead.owner
                ? <><Avatar name={lead.owner} size={22} />{lead.owner}</>
                : <span className="pill pill-amber"><i className="bi bi-inbox" aria-hidden="true" />Queue</span>}
            </div>
          </div>
        </div>
      </div>

      {/* ---- Sales path ---- */}
      {!isConverted && (
        <>
          <div style={{ border: '1px solid var(--line)', borderTop: 'none', background: '#fff', padding: '14px 18px 0' }}>
            <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
              <ul className="path flex-grow-1">
                {PATH.map((s, i) => (
                  <li key={s}>
                    <span className={`step ${i < pathIndex ? 'done' : i === pathIndex ? (s === 'Converted' ? 'won' : 'current') : ''}`}>
                      {i < pathIndex && <i className="bi bi-check-lg" aria-hidden="true" />}{s}
                    </span>
                  </li>
                ))}
              </ul>
              {canAdvance && (
                <button type="button" className="btn btn-save btn-sm-x no-wrap" disabled={update.isPending} onClick={advance}>
                  <i className="bi bi-check-lg me-1" aria-hidden="true" />Mark Status as Complete
                </button>
              )}
            </div>
            <button
              type="button" className="btn btn-ghost btn-sm-x w-100 mb-0"
              style={{ border: 'none', borderRadius: 0 }}
              onClick={() => setGuideOpen((v) => !v)}
              aria-expanded={guideOpen}
            >
              <i className={`bi ${guideOpen ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`} style={{ fontSize: 10 }} aria-hidden="true" />
              Key Fields &amp; Guidance for Success
            </button>
          </div>
          {guideOpen && (
            <div className="path-guide mb-3">
              <div className="row g-4">
                <div className="col-lg-5">
                  <div className="guide-key">Key fields for this stage</div>
                  <div className="field-row"><div className="fk">Budget</div><div className="fv">
                    {lead.qualification?.has_budget
                      ? <span className="pill pill-green"><i className="bi bi-check-lg" aria-hidden="true" />Confirmed</span>
                      : <span className="pill pill-amber">Not confirmed</span>}
                  </div></div>
                  <div className="field-row"><div className="fk">Authority</div><div className="fv">
                    {lead.qualification?.has_authority ? 'Decision maker confirmed' : 'Not confirmed'}
                  </div></div>
                  <div className="field-row"><div className="fk">Timeline</div><div className="fv">
                    {lead.qualification?.timeline_confirmed
                      ? <span className="pill pill-green">Confirmed</span>
                      : <span className="pill pill-amber">Not confirmed</span>}
                  </div></div>
                  <div className="field-row"><div className="fk">Need</div><div className="fv">{lead.qualification?.requirement_summary || '—'}</div></div>
                </div>
                <div className="col-lg-7">
                  <div className="guide-key">Guidance for success</div>
                  <div className="row g-2 t-sm">
                    <div className="col-md-6">
                      <div className="d-flex gap-2 mb-2"><i className="bi bi-check-circle-fill text-success mt-1" aria-hidden="true" /><div>Confirm the business problem in the customer&rsquo;s own words</div></div>
                      <div className="d-flex gap-2 mb-2"><i className="bi bi-check-circle-fill text-success mt-1" aria-hidden="true" /><div>Identify and meet the economic buyer</div></div>
                      <div className="d-flex gap-2"><i className="bi bi-check-circle-fill text-success mt-1" aria-hidden="true" /><div>Validate budget range and approval path</div></div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex gap-2 mb-2"><i className="bi bi-circle ink-4 mt-1" aria-hidden="true" /><div>Pin down the purchase timeline against their budget cycle</div></div>
                      <div className="d-flex gap-2 mb-2"><i className="bi bi-circle ink-4 mt-1" aria-hidden="true" /><div>Map the technical evaluator, if one is not yet identified</div></div>
                      <div className="d-flex gap-2"><i className="bi bi-circle ink-4 mt-1" aria-hidden="true" /><div>Agree the next step in writing before converting</div></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ---- Body ---- */}
      <div className="row g-3">
        <div className="col-xl-3 order-xl-1">
          <ScoreCard lead={lead} />
          <RelatedLists lead={lead} />
        </div>

        <div className="col-xl-6 order-xl-2">
          <Card bare className="fade-up">
            <div className="px-3 pt-3">
              <ul className="nav nav-tabs-x">
                {TABS.map((t) => (
                  <li key={t.key} className="nav-item">
                    <a
                      className={`nav-link ${tab === t.key ? 'active' : ''}`}
                      href="#" onClick={(e) => { e.preventDefault(); setTab(t.key); }}
                    >
                      {t.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-body-x">
              {tab === 'activity' && <ActivityTab linkField="lead_id" recordId={lead.id} />}
              {tab === 'details' && <DetailsTab lead={lead} options={options} canEdit={canEdit} edits={edits} onEdit={onEdit} />}
              {tab === 'news' && <NewsTab company={lead.company} />}
            </div>
          </Card>
        </div>

        <div className="col-xl-3 order-xl-3">
          <EngagementCard lead={lead} />
          <NextStepCard />
          <div ref={dupRef}><DuplicateCard lead={lead} /></div>
        </div>
      </div>

      <LeadFormModal
        open={editOpen}
        lead={lead}
        busy={update.isPending}
        options={options}
        onClose={() => setEditOpen(false)}
        onSubmit={async (patch) => {
          await update.mutateAsync({ id: lead.id, patch, rowVersion: lead.raw.row_version });
          setEditOpen(false);
        }}
      />

      <ConvertModal
        open={convertOpen}
        lead={lead}
        options={options}
        busy={convert.isPending}
        onClose={() => setConvertOpen(false)}
        onConfirm={async (body) => {
          const result = await convert.mutateAsync({ id: lead.id, options: body });
          setConvertOpen(false);
          if (result.opportunity_id) navigate('/opportunities');
          else navigate('/leads');
        }}
      />

      <DeleteLeadModal
        open={deleting}
        rows={[lead]}
        busy={remove.isPending}
        onClose={() => setDeleting(false)}
        onConfirm={async (reason) => {
          await remove.mutateAsync({ id: lead.id, reason, rowVersion: lead.raw.row_version, label: lead.name });
          setDeleting(false);
          navigate('/leads');
        }}
      />

      <ChangeOwnerModal
        open={assigningOpen}
        rows={[lead]}
        options={options.owners}
        busy={assign.isPending}
        onClose={() => setAssigningOpen(false)}
        onConfirm={async ({ ownerId, reason }) => {
          await assign.mutateAsync({ ids: [lead.id], ownerId, reason }).catch(() => {});
          setAssigningOpen(false);
        }}
      />

      <NotBuiltModal open={Boolean(notBuilt)} action={notBuilt} onClose={() => setNotBuilt(null)} />

      <AddToCampaignModal open={campaignAddOpen} leadIds={[id]} onClose={() => setCampaignAddOpen(false)} />

      <EmailComposerModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        anchor={{ lead_id: lead.id }}
        defaultEmail={lead.email}
      />
    </>
  );
}

function HeaderMenu({ canAssign, onOwner, onClone, onCampaign, onFindDuplicates, onDelete }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  /* Same outside-click/Escape convention as `LeadsListPage`'s `OverflowMenu` —
     a ref and `.contains()`, not a full-viewport backdrop div. The backdrop
     approach raced with the list's own state updates and occasionally ate the
     very click meant to open the menu. */
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const printNow = () => {
    if (typeof window !== 'undefined') window.print();
  };

  const item = (icon, label, onClick, danger = false) => (
    <li>
      <button
        type="button"
        className={`dropdown-item ${danger ? 'danger' : ''}`}
        onClick={() => { setOpen(false); onClick(); }}
      >
        <i className={`bi ${icon} me-2`} aria-hidden="true" />{label}
      </button>
    </li>
  );

  return (
    <div className="dropdown" ref={box}>
      <button type="button" className="btn btn-soft btn-sm-x" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <i className="bi bi-caret-down-fill" style={{ fontSize: 10 }} aria-hidden="true" />
      </button>
      <ul className={`dropdown-menu dropdown-menu-end ${open ? 'show' : ''}`}>
        {canAssign && item('bi-person-check', 'Change Owner', onOwner)}
        {item('bi-files', 'Clone', onClone)}
        {item('bi-megaphone', 'Add to Campaign', onCampaign)}
        {item('bi-people', 'Find Duplicates', onFindDuplicates)}
        {item('bi-printer', 'Print', printNow)}
        {onDelete && (
          <>
            <li><hr className="dropdown-divider" /></li>
            {item('bi-trash', 'Delete', onDelete, true)}
          </>
        )}
      </ul>
    </div>
  );
}
